import React, {useCallback} from 'react'

import type {CodeLineData} from '../components/blob/BlobContent/Code/hooks/use-code-lines'
import {useCurrentBlame} from './CurrentBlame'
import {useCurrentBlob} from './CurrentBlob'

const CurrentLineRefMapContext = React.createContext<Map<number, CodeLineData[]> | null>(null)

const useBlobAndBlameSpecificMap = () => {
  const blob = useCurrentBlob()
  const blame = useCurrentBlame()

  const [map, setMap] = React.useState<Map<number, CodeLineData[]> | null>(() => new Map<number, CodeLineData[]>())
  const [prevBlob, setPrevBlob] = React.useState(blob)
  const [prevBlame, setPrevBlame] = React.useState(blame)

  if (blob !== prevBlob || blame !== prevBlame) {
    const nextMap = blame ? null : new Map<number, CodeLineData[]>()
    setMap(nextMap)
    setPrevBlob(blob)
    setPrevBlame(blame)
    return nextMap
  }

  return map
}

export function CurrentLineRefMapProvider({children}: React.PropsWithChildren) {
  const map = useBlobAndBlameSpecificMap()
  return <CurrentLineRefMapContext value={map}> {children} </CurrentLineRefMapContext>
}

export function useLineElementMap() {
  return React.use(CurrentLineRefMapContext)
}

export function useGetValueFromLineElementMap() {
  const map = React.use(CurrentLineRefMapContext)
  return useCallback((num: number) => map?.get(num), [map])
}

export function useAddValueToLineElementMap() {
  const map = React.use(CurrentLineRefMapContext)
  return useCallback(
    (num: number, elem: CodeLineData) => {
      if (!map) return
      if (map.has(num)) {
        map.get(num)?.push(elem)
      } else {
        map.set(num, [elem])
      }
    },
    [map],
  )
}
